import React, { useEffect, useRef, useState } from 'react';
import jwt_decode from 'jwt-decode';
import * as _ from 'lodash';

import { IonContent, RefresherEventDetail } from '@ionic/react';

import { Factory } from '../factory';
import { useSelector } from 'react-redux';
import { getMetaResolver, getResolver } from './resolvers.registry';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, setNavigate, Inspector } from '@flexo/atoms';
import { getSiteLayoutHelper } from './helpers';
import { auth, IconWrapper, useSafeArea } from '@flexo/general';
import { useDispatch } from 'react-redux';
import { ALERT, THEME } from '@flexo/reducers';
import { Keyboard } from '@capacitor/keyboard';

const ENV = (import.meta as any).env;

export const Modules = (props: any) => {
  const navigate = useNavigate();
  setNavigate(navigate);
  const params = useParams();
  const alertStore = useSelector((state: any) => state.alert);

  const user = useSelector((state: any) => state.user);
  const metadata = useSelector((state: any) => state.metadata);
  const theme = useSelector((state: any) => state.theme);

  const location = useLocation();
  const dispatch = useDispatch();
  const pathSegment = location.pathname.split('/')[1];

  const [activeModule, setActiveModule] = useState<any | null>(null);
  const [layouts, setLayouts] = useState<any | null>(null);
  const [widgets, setWidgets] = useState<any | null>(null);

  const [shownInspector, setShownInspector] = useState<any>(null);
  const [devModeActive, setDevModeActive] = useState<any>(false);
  const [showSplashScreen, setShowSplashScreen] = useState<any>(true);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const scrollRef = useRef<HTMLIonContentElement>(null);

  const { isAndroid, isIPhone16OrDynamicIslandDevice } = useSafeArea();

  function scrollToTop() {
    if (ENV?.VITE_APP_IS_WEB === 'true') {
      scrollRef.current?.scrollToTop(500);
    }
  }

  useEffect(() => {
    scrollToTop();
  }, [location.pathname]);

  function getFilteredWidgets(activeModule) {
    let authContent: any = null;

    try {
      const accessToken =
        (auth as any)?.currentUser?.accessToken ||
        (auth as any)?.currentUser?.stsTokenManager?.accessToken ||
        user?.tokenContent?.accessToken ||
        user?._tokenResponse?.idToken;

      if (accessToken) {
        authContent = jwt_decode(accessToken);
      }
    } catch (err: any) {
      console.warn('Token decode failed in widget filtering:', err.message);
    }

    const siteType: any =
      getSiteLayoutHelper(
        metadata,
        pathSegment,
        params,
        activeModule,
        props.MODULE
      ) || 'consumer';
    const role =
      authContent?.user_role || user?.tokenContent?.user_role || 'default';

    const defaultWidgets = _.keyBy(activeModule?.widgets?.default, 'id');
    const activeWidgets: any = _.keyBy(
      activeModule?.widgets?.[role]?.[siteType] || {},
      'id'
    );
    // Merge default and active widgets by id: update default properties with active ones
    const allIds = Array.from(
      new Set([...Object.keys(defaultWidgets), ...Object.keys(activeWidgets)])
    );
    const mergedWidgetsById: Record<string, any> = {};
    allIds.forEach((id) => {
      mergedWidgetsById[id] = {
        ...defaultWidgets[id],
        ...activeWidgets[id],
      };
    });
    const _widgets = Object.values(mergedWidgetsById)
      .filter((widget: any) => widget?.keep !== false)
      .sort((a: any, b: any) => a.order - b.order);

    return _widgets;
  }

  function handleRefresh(event: CustomEvent<RefresherEventDetail>) {
    setTimeout(() => {
      // TODO: Z - There is basically almost never a good reason to have a timeout, it spawns a new thread and is not reliable

      // This is just to test the pull to refresh functionality, if it closes after 2 seconds or not.
      // Not the actual code XD

      // Any calls to load data go here

      //create function that check metadataapi store and calendar sotre and calls http service

      event.detail.complete();
    }, 2000);
  }

  function showInspector(widget: any) {
    if (ENV.VITE_APP_ALLOW_INSPECTION !== 'true') {
      return;
    }
    if (devModeActive === false) {
      return;
    }

    const _widget: any = {
      ...widget,
    };

    delete _widget.layouts;
    delete _widget.setActiveModule;
    delete _widget.showInspector;

    setShownInspector(_widget);
  }

  const Resolvers = React.useMemo(() => {
    if (activeModule === null) return null;

    return activeModule?.apis?.default?.map((resolver: any) => {
      const Resolver = getResolver(resolver);
      return Resolver;
    });
  }, [activeModule]);

  const MetaResolvers = React.useMemo(() => {
    if (activeModule === null) return null;
    return activeModule?.metaResolvers?.default?.map((resolver: any) => {
      const MetaResolver = getMetaResolver(resolver);
      return MetaResolver;
    });
  }, [activeModule]);

  // setting colors
  useEffect(() => {
    if (Object.keys(theme?.colors)?.length > 0) return;
    dispatch(THEME.SetColors({ colors: props.COLORS } as any));
  }, []);

  useEffect(() => {
    console.log('props.MODULE', props.MODULE);
    if (!_.isEqual(props.MODULE, activeModule)) {
      const _module = _.cloneDeep(props.MODULE);
      setActiveModule(_module);
    }
  }, [props?.MODULE?.name]);

  useEffect(() => {
    if (activeModule === null) return;
    const _widgets = getFilteredWidgets(activeModule);
    if (!_.isEqual(_widgets, widgets)) {
      if (
        ENV.VITE_APP_IS_WEB === 'true' &&
        metadata?.EntitiesAsyncStatus &&
        metadata?.EntitiesAsyncStatus === 'PENDING' &&
        user?.isLoggingOut !== true
      ) {
        setShowSplashScreen(true);
      }

      setWidgets(_widgets);

      if (ENV.VITE_APP_IS_WEB === 'true' && user?.isLoggingOut !== true) {
        setTimeout(() => setShowSplashScreen(false), 500);
      }
    }
  }, [activeModule, metadata]);

  useEffect(() => {
    if (
      ENV?.VITE_APP_DISABLE_SCREEN_SIZE_ALERT === 'true' &&
      ENV?.VITE_APP_IS_WEB === 'true'
    ) {
      return;
    }

    if (!ENV?.VITE_APP_IS_WEB || ENV?.VITE_APP_IS_WEB === 'false') {
      return;
    }

    if (windowWidth < 911) {
      dispatch(
        ALERT.SetAlert({
          type: 'ALERT_SCREEN_SIZE',
        } as any)
      );
    } else if (
      windowWidth > 911 &&
      alertStore.alert.type === 'ALERT_SCREEN_SIZE'
    ) {
      dispatch(ALERT.ResetAlert());
    }
  }, [windowWidth, alertStore.alert.type]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getScroll = () => {
    if (ENV?.VITE_APP_IS_WEB === 'true') return true;
    if (
      pathSegment === 'household' ||
      pathSegment === 'community' ||
      (pathSegment === '' && ENV?.VITE_APP_IS_WEB !== 'true')
    )
      return true;
    return false;
  };

  return (
    <>
      {ENV.VITE_APP_ALLOW_INSPECTION === 'true' && (
        <Inspector
          {...{
            setShownInspector,
            shownInspector,
            devModeActive,
            setDevModeActive,
          }}
        />
      )}

      {ENV.VITE_APP_IS_WEB === 'true' && showSplashScreen ? (
        <div
          className="flex flex-align-center flex-justify-center flex-column "
          style={{
            height: '100vh',
            width: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 9999999999999999999,
            backgroundColor: '#f7f7f7',
          }}
        >
          <IconWrapper
            iconName={ENV?.VITE_APP_IS_WEB === 'true' ? 'logo_wide' : 'logo'}
          />
        </div>
      ) : null}

      {activeModule && (
        <>
          {Resolvers?.map((Resolver: any) => (
            <Resolver key={Resolver} />
          ))}
          {MetaResolvers?.map((MetaResolver: any) => (
            <MetaResolver key={MetaResolver} />
          ))}
        </>
      )}

      {activeModule && widgets ? (
        <IonContent scrollY={getScroll()} ref={scrollRef}>
          {/* Add safe area padding container */}
          <div
            className={` ${
              ENV?.VITE_APP_IS_WEB === 'true'
                ? 'WidgetsContainer'
                : 'WidgetsContainer__Mobile'
            }`}
            style={
              activeModule.needsAuth && ENV?.VITE_APP_IS_WEB === 'false'
                ? {
                    paddingTop: 'env(safe-area-inset-top, -10px)',
                    boxSizing: 'border-box',
                  }
                : {}
            }
          >
            {widgets.length > 0 &&
              widgets.map((widget: any, widget_i: number) => {
                return Factory({
                  ...widget,
                  layouts: layouts,
                  setActiveModule,
                  showInspector,
                  normalizeRender: true,
                  isAndroid,
                  isIPhone16OrDynamicIslandDevice,
                } as any);
              })}
          </div>
          {alertStore.alert.visible && <Alert />}
        </IonContent>
      ) : null}
    </>
  );
};

export default React.memo(Modules);
